import { CurrencyDisplayUtil } from './../../common/currency-display-util';
import {
    GetSurvivalHorizonServiceDataOutput
} from '@liquitics/transaction';
import { UserProfileMultiEntityNostro } from '@liquitics/transaction';
import { hyphenatedMonthDay } from '@liquitics/transaction';

import { AppConfig } from './../../app.config';
import { DashboardEventService } from './../../dashboard/dashboard-event.service';
import { SurvivalHorizonService } from './../survival-horizon.service';
import { Subscription } from 'rxjs';
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { FiveDayLadderSelection } from './five-day-ladder-selection';

@Component({
    selector: 'app-five-day-ladder',
    templateUrl: './five-day-ladder.component.html',
    styleUrls: ['./five-day-ladder.component.css']
})
export class FiveDayLadderComponent implements OnInit, OnDestroy {
    @Input()
    title: string;

    tableData: any = [];
    treeData: TreeNode[];

    selectedRow: any;
    headerDates: string[] = [];
    dates: string[] = [];
    private dataStreamDisposable: Subscription;
    private desks: string[];
    locationName: string;
    currentFirstShownRowIndex = 0;
    nonProfileCurrencies: string[] = [];

    constructor(private survivalHorizonService: SurvivalHorizonService,
        private dashboardEventService: DashboardEventService,
        private appConfig: AppConfig) {
        const profile: UserProfileMultiEntityNostro = this.appConfig.activeUserProfile as UserProfileMultiEntityNostro;
        this.desks = profile.getDesks();
        this.locationName = profile.locationName;
    }

    ngOnInit() {
        const m = this.appConfig.getTodayStartUTC().clone();
        for (let i = 0; i < 5; i++) {
            this.headerDates.push(hyphenatedMonthDay(m));
            this.dates.push(m.format('YYYY-MM-DD'));
            m.add(1, 'days');
        }
        this.dataStreamDisposable = this.survivalHorizonService.getPollingDataSubject()
            .subscribe({
                next: (data: GetSurvivalHorizonServiceDataOutput) => {
                    let actualBalancesByCcy = {};
                    if (data.actualBalances) {
                        actualBalancesByCcy = data.actualBalances.reduce((lookup, ab) => {
                            lookup[ab.currency] = ab.amount;
                            return lookup;
                        }, {});
                    }
                    if (data.fiveDayLadder) {
                        const sortedCurrencies = data.fiveDayLadder
                            .map((position: any) => {
                                return position.currency;
                            })
                            .sort((c1: string, c2: string) => {
                                return this.compareCurrencies(c1, c2);
                            })
                            .filter((currency: any, ccyIndex: number, currencies: any[]) => {
                                return !ccyIndex || currency !== currencies[ccyIndex - 1];
                            })
                            ;
                        const positions = this.survivalHorizonService.computeAndScale(data.fiveDayLadder, true)
                            .map((position: any) => {
                                const desk = position.desk;
                                let location;
                                const order = sortedCurrencies.indexOf(position.currency);
                                if (this.desks.includes(desk)) {
                                    location = this.locationName;
                                } else {
                                    location = 'Other';
                                }
                                const currency = position.currency;
                                const ladderEntry = {
                                    location: location,
                                    currency: currency,
                                    amount: position.inAmount - position.outAmount,
                                    inAmount: position.inAmount,
                                    outAmount: position.outAmount,
                                    date: position.bucket,
                                    order: order,
                                    balanceAmount: position.currencyBalanceSnapshotAmount,
                                    actualAmount:
                                        CurrencyDisplayUtil.toShortNumber(actualBalancesByCcy[currency], 2),
                                }
                                return ladderEntry;
                            })
                        this.updateTableData(positions);
                    }
                }, error: (err) => {
                    console.log(err);
                    throw err;
                }
            });
    }

    private updateTableData(positions: any[]) {
        if (positions) {
            const groupedBySignificantMember = positions
                .map((f: any) => {
                    let cumulativeAmount = 0.0;
                    this.dates.forEach((d: string, index: number) => {
                        let balanceAmount = 0.0;
                        if (d === f.date) {
                            cumulativeAmount += f.amount;
                            if (index === 0) {
                                balanceAmount = f.balanceAmount || 0.0;
                                cumulativeAmount += balanceAmount;
                            }
                        }
                        let locationAmount = 0.0;
                        let otherAmount = 0.0;
                        if (f.location === this.locationName) {
                            locationAmount = cumulativeAmount;
                        } else {
                            otherAmount = cumulativeAmount;
                        }
                        f['day' + (index + 1) + 'LocationAmount'] = locationAmount;
                        f['day' + (index + 1) + 'OtherAmount'] = otherAmount;
                    })
                    let todayInAmount = 0.0;
                    let todayOutAmount = 0.0;
                    if (f.date === this.dates[0]) {
                        todayInAmount = f.inAmount;
                        todayOutAmount = f.outAmount;
                        if (f.balanceAmount > 0) {
                            todayInAmount += f.balanceAmount || 0;
                        } else {
                            todayOutAmount += f.balanceAmount || 0;
                        }
                    }
                    f.todayInAmount = todayInAmount;
                    f.todayOutAmount = todayOutAmount;
                    return f;
                })
                .reduce((grouped, f) => {
                    const key = f.currency; // + f.location;
                    grouped[key] = grouped[key] || [];
                    grouped[key].push(f);
                    return grouped;
                }, {});
            this.tableData = Object.keys(groupedBySignificantMember)
                .map((key: string) => {
                    return groupedBySignificantMember[key].reduce((prev, curr) => {
                        for (let i = 1; i <= this.dates.length; i++) {
                            prev['day' + i + 'LocationAmount'] += curr['day' + i + 'LocationAmount'];
                            prev['day' + i + 'OtherAmount'] += curr['day' + i + 'OtherAmount'];
                        }
                        prev.todayInAmount += curr.todayInAmount;
                        prev.todayOutAmount += curr.todayOutAmount;
                        return prev;
                    });
                })
                .sort((r1: any, r2: any) => {
                    return this.compareCurrencies(r1.currency, r2.currency);
                })
            // .map((r: any) => {
            //     const data: any = {};
            //     const node = {
            //         data: data
            //     } as TreeNode;
            //     for (let i = 1; i <= this.dates.length; i++) {
            //         data['day' + i + 'Amount'] += r['day' + i + 'Amount'];
            //     }
            //     return node;
            // })
            if (!this.selectedRow && this.tableData.length > 0) {
                this.selectedRow = this.tableData[0];
                this.sendSelection(this.selectedRow);
            } else {
                if (this.selectedRow) {
                    this.selectedRow = this.tableData[this.selectedRow.order];
                }
            }
        }
    }

    onSort(event) {
        if (event.field === 'currency') {
            event.data.sort((r1: any, r2: any) => {
                const ccyCompare = this.compareCurrencies(r1.currency, r2.currency);
                let result;
                if (ccyCompare === 0) {
                    result = (r1.location === this.locationName) ? -1 : 1;
                } else {
                    result = ccyCompare;
                }
                return (event.order * result);
            })
        }
    }

    onRowSelect(event) {
        if (event.data) {
            this.sendSelection(event.data);
        }
    }

    private compareCurrencies(ccy1: string, ccy2: string): number {
        const currencies = this.appConfig.activeUserProfile.getCurrencies();
        const ci1 = this.currencyOrder(ccy1);
        const ci2 = this.currencyOrder(ccy2);
        const ccyCompare = ci1 - ci2;
        return ccyCompare;
    }

    private currencyOrder(ccy: string): number {
        let ccyIndex = this.appConfig.activeUserProfile.getCurrencies().indexOf(ccy);
        if (ccyIndex === -1) {
            ccyIndex = this.nonProfileCurrencies.indexOf(ccy);
            if (ccyIndex === -1) {
                ccyIndex = this.appConfig.activeUserProfile.getCurrencies().length + this.nonProfileCurrencies.length;
                this.nonProfileCurrencies.push(ccy);
            } else {
                ccyIndex = this.appConfig.activeUserProfile.getCurrencies().length + ccyIndex;
            }
        }
        return ccyIndex;
    }

    private sendSelection(row: any) {
        this.survivalHorizonService.setSelectedCurrency(row.currency);
        this.dashboardEventService.currencySelectionSubject.next(row.currency);
        const selection = new FiveDayLadderSelection();
        selection.currency = row.currency;
        selection.inAmount = row.todayInAmount;
        selection.outAmount = row.todayOutAmount;
        this.dashboardEventService.fiveDayLadderRowSelectionSubject.next(selection);
    }

    ngOnDestroy() {
        if (this.dataStreamDisposable) {
            this.dataStreamDisposable.unsubscribe();
        }
    }
    round(value: number): string {
        const rounded = Math.round(value);
        let formatted = String(rounded);
        if (rounded < 0) {
            formatted = '(' + formatted + ')';
        }
        return formatted;
    }
}


